import { formatDate, formatMoney, titleCase } from "./format";

const PAPER_PROFILES = {
  58: { paper: 58, printable: 50, font: 10, title: 15, gap: 2 },
  80: { paper: 80, printable: 72, font: 11, title: 17, gap: 3 },
};

function normalizePaperWidth(value) {
  return Number(value) === 58 ? 58 : 80;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function receiptDocument({ title, storeName, subtitle, metaHtml, linesHtml, summaryHtml, footer, paperWidthMm }) {
  const width = normalizePaperWidth(paperWidthMm);
  const profile = PAPER_PROFILES[width];

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(title || "ABOS Receipt")}</title>
<style>
  @page { margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }
  body {
    width: ${profile.paper}mm;
    padding: ${profile.gap}mm;
    font-family: "Courier New", Courier, monospace;
    font-size: ${profile.font}px;
    line-height: 1.35;
  }
  .receipt { width: ${profile.printable}mm; margin: 0 auto; }
  .center { text-align: center; }
  .store { font-family: Arial, sans-serif; font-size: ${profile.title}px; font-weight: 800; line-height: 1.15; }
  .muted { color: #333; font-size: 0.9em; }
  .rule { border-top: 1px dashed #000; margin: 2.2mm 0; }
  .meta-row, .summary-row { display: flex; justify-content: space-between; gap: 2mm; }
  .meta-row span:first-child, .summary-row span:first-child { flex: 0 0 auto; }
  .meta-row span:last-child, .summary-row span:last-child { text-align: right; overflow-wrap: anywhere; }
  .line { margin: 2mm 0; }
  .line-name { font-weight: 700; overflow-wrap: anywhere; }
  .line-sub { display: flex; justify-content: space-between; gap: 2mm; margin-top: 0.6mm; }
  .line-sub span:last-child { text-align: right; }
  .return { margin-top: 1.5mm; }
  .total { font-size: 1.15em; font-weight: 800; }
  .footer { margin-top: 3mm; text-align: center; font-size: 0.9em; }
  @media screen {
    body { margin: 12px auto; box-shadow: 0 0 0 1px #ddd; min-height: 60mm; }
  }
  @media print {
    html, body { width: ${profile.paper}mm; }
    body { box-shadow: none; }
  }
</style>
</head>
<body>
  <div class="receipt">
    <div class="center">
      <div class="store">${escapeHtml(storeName || "ABOS Store")}</div>
      ${subtitle ? `<div class="muted">${escapeHtml(subtitle)}</div>` : ""}
    </div>
    <div class="rule"></div>
    ${metaHtml || ""}
    <div class="rule"></div>
    ${linesHtml || ""}
    <div class="rule"></div>
    ${summaryHtml || ""}
    ${footer ? `<div class="footer">${escapeHtml(footer)}</div>` : ""}
  </div>
</body>
</html>`;
}

function openPrintWindow({ title, html, paperWidthMm }) {
  const width = normalizePaperWidth(paperWidthMm);
  const popupWidth = width === 58 ? 360 : 460;
  const printWindow = window.open("", "_blank", `width=${popupWidth},height=760`);
  if (!printWindow) return false;

  try {
    printWindow.opener = null;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.document.title = title || "ABOS Receipt";
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 180);
    return true;
  } catch {
    try { printWindow.close(); } catch { /* ignore */ }
    return false;
  }
}

function itemLines(items, currency) {
  return (items || []).map((item) => `
    <div class="line">
      <div class="line-name">${escapeHtml(item.product_name || "Product")}</div>
      ${item.variant_label ? `<div class="muted">${escapeHtml(item.variant_label)}</div>` : ""}
      ${item.sku ? `<div class="muted">SKU ${escapeHtml(item.sku)}</div>` : ""}
      <div class="line-sub">
        <span>${escapeHtml(item.quantity)} × ${escapeHtml(formatMoney(item.unit_price, currency))}</span>
        <strong>${escapeHtml(formatMoney(item.line_total, currency))}</strong>
      </div>
    </div>`).join("");
}

export function printNativePosReceipt(sale, options = {}) {
  const paperWidthMm = normalizePaperWidth(options.paperWidthMm);
  const provisional = Boolean(sale?.offline_pending);
  const metaHtml = `
    <div class="meta-row"><span>${provisional ? "Provisional" : "Invoice"}</span><span>${escapeHtml(sale?.invoice_number || sale?.sale_public_id || "—")}</span></div>
    <div class="meta-row"><span>Date</span><span>${escapeHtml(formatDate(sale?.sold_at, ""))}</span></div>
    <div class="meta-row"><span>Payment</span><span>Cash</span></div>
    ${provisional ? '<div class="meta-row"><span>Sync</span><span>Pending ABOS sync</span></div>' : ""}`;
  const summaryHtml = `
    ${!provisional && Number(sale?.discount_total || 0) > 0 ? `
      <div class="summary-row"><span>Subtotal</span><span>${escapeHtml(formatMoney(sale?.subtotal, sale?.currency))}</span></div>
      <div class="summary-row"><span>Discount</span><span>-${escapeHtml(formatMoney(sale?.discount_total, sale?.currency))}</span></div>` : ""}
    <div class="summary-row total"><span>TOTAL</span><span>${escapeHtml(formatMoney(sale?.total_amount, sale?.currency))}</span></div>`;
  const html = receiptDocument({
    title: sale?.invoice_number || "ABOS Receipt",
    storeName: options.storeName,
    subtitle: provisional
      ? "PROVISIONAL OFFLINE RECEIPT · Pending ABOS sync"
      : "ABOS Point of Sale",
    metaHtml,
    linesHtml: itemLines(sale?.items, sale?.currency),
    summaryHtml,
    footer: provisional
      ? "Offline sale stored on this device. Canonical ABOS invoice follows after sync."
      : "Thank you.",
    paperWidthMm,
  });
  return openPrintWindow({ title: sale?.invoice_number, html, paperWidthMm });
}

export function printCanonicalSaleReceipt(detail, options = {}) {
  const paperWidthMm = normalizePaperWidth(options.paperWidthMm);
  const metaHtml = `
    <div class="meta-row"><span>Invoice</span><span>${escapeHtml(detail?.invoice_number || detail?.public_id || "—")}</span></div>
    <div class="meta-row"><span>Date</span><span>${escapeHtml(formatDate(detail?.sold_at, ""))}</span></div>
    <div class="meta-row"><span>Status</span><span>${escapeHtml(titleCase(detail?.status || "unknown"))}</span></div>
    <div class="meta-row"><span>Payment</span><span>${escapeHtml(titleCase(detail?.payment_status || "unknown"))}</span></div>
    ${detail?.cashier_name || detail?.employee_name ? `<div class="meta-row"><span>Cashier</span><span>${escapeHtml(detail.cashier_name || detail.employee_name)}</span></div>` : ""}`;

  const returns = (detail?.returns || []).map((row) => `
    <div class="line return">
      <div class="line-name">RETURN · ${escapeHtml(row.product_name || "Item")}</div>
      ${row.variant_label ? `<div class="muted">${escapeHtml(row.variant_label)}</div>` : ""}
      ${row.sku ? `<div class="muted">SKU ${escapeHtml(row.sku)}</div>` : ""}
      <div class="line-sub">
        <span>Qty ${escapeHtml(row.quantity == null ? "—" : Number(row.quantity).toLocaleString())}</span>
        <strong>-${escapeHtml(formatMoney(row.amount, detail?.currency))}</strong>
      </div>
    </div>`).join("");

  const summaryHtml = `
    <div class="summary-row"><span>Gross sale</span><strong>${escapeHtml(formatMoney(detail?.total_amount, detail?.currency))}</strong></div>
    <div class="summary-row"><span>Refunded</span><strong>-${escapeHtml(formatMoney(detail?.refund_total, detail?.currency))}</strong></div>
    <div class="summary-row total"><span>NET</span><span>${escapeHtml(formatMoney(detail?.net_total, detail?.currency))}</span></div>`;

  const html = receiptDocument({
    title: detail?.invoice_number || "ABOS Receipt",
    storeName: options.storeName,
    subtitle: "ABOS sales receipt",
    metaHtml,
    linesHtml: `${itemLines(detail?.items, detail?.currency)}${returns}`,
    summaryHtml,
    footer: "Returns remain linked to the original sale.",
    paperWidthMm,
  });
  return openPrintWindow({ title: detail?.invoice_number, html, paperWidthMm });
}

export function printTestReceipt(options = {}) {
  const paperWidthMm = normalizePaperWidth(options.paperWidthMm);
  const metaHtml = `
    <div class="meta-row"><span>Test</span><span>Printer setup</span></div>
    <div class="meta-row"><span>Paper</span><span>${paperWidthMm} mm</span></div>
    <div class="meta-row"><span>Date</span><span>${escapeHtml(new Date().toLocaleString())}</span></div>`;
  const linesHtml = `
    <div class="line">
      <div class="line-name">ABOS Thermal Printer Test</div>
      <div class="muted">If this line is readable and not clipped, the paper profile is correct.</div>
      <div class="line-sub"><span>1 × Test item</span><strong>$1.00</strong></div>
    </div>`;
  const summaryHtml = `<div class="summary-row total"><span>TEST TOTAL</span><span>$1.00</span></div>`;
  const html = receiptDocument({
    title: "ABOS Printer Test",
    storeName: options.storeName,
    subtitle: "Hardware setup",
    metaHtml,
    linesHtml,
    summaryHtml,
    footer: "ABOS printer test · No sale was created.",
    paperWidthMm,
  });
  return openPrintWindow({ title: "ABOS Printer Test", html, paperWidthMm });
}
